import { useState, useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, Star, ArrowRight, Play, Camera, FileText, Wand2, BookOpen, Printer, Gift, Menu, X, ShieldCheck, Truck, Heart, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { PACKAGES, WORLDS, T, tr } from "../content";
import { IMG } from "../lib/api";
import { useLang } from "../App";
import OrderForm from "../components/OrderForm";
import VideoLightbox from "../components/VideoLightbox";
import { PromoBar, InteriorGallery, QualitySection, OccasionsSection, ReviewsMarquee, NewsletterSection } from "../components/Extras";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else if (el) el.scrollIntoView({ behavior: "smooth" });
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
};

export default function Landing() {
  const { lang, setLang } = useLang();
  const t = (o) => tr(o, lang);
  const [selected, setSelected] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scene, setScene] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bookY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bookScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const openForm = (pkg) => { setSelected(pkg); setMenuOpen(false); };
  const openDefault = () => scrollTo("pricing");

  const line1 = t(T.hero.line1), line2 = t(T.hero.line2);

  return (
    <div className="relative">
      <PromoBar lang={lang} />
      {/* HEADER */}
      <header className="fixed top-9 inset-x-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/70 border-b border-[#E7E0D4]/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={() => scrollTo("top")} data-testid="logo" className="flex items-center gap-2">
            <Sparkles className="text-[#D4AF37]" size={20} strokeWidth={1.5} />
            <span className="font-display text-2xl tracking-tight">BOHATERO</span>
          </button>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <button onClick={() => scrollTo("how")} data-testid="nav-how" className="hover:text-[#D4AF37] transition-colors">{t(T.nav.how)}</button>
            <button onClick={() => scrollTo("sample")} data-testid="nav-sample" className="hover:text-[#D4AF37] transition-colors">{t(T.nav.sample)}</button>
            <button onClick={() => scrollTo("pricing")} data-testid="nav-pricing" className="hover:text-[#D4AF37] transition-colors">{t(T.nav.pricing)}</button>
            <button onClick={() => scrollTo("faq")} data-testid="nav-faq" className="hover:text-[#D4AF37] transition-colors">{t(T.nav.faq)}</button>
          </nav>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-full border border-[#E7E0D4] overflow-hidden text-xs font-semibold">
              {["pl", "en"].map((l) => (
                <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`} className={`px-3 py-1.5 transition-colors ${lang === l ? "bg-[#1A243A] text-[#FDFBF7]" : "text-[#64748B]"}`}>{l.toUpperCase()}</button>
              ))}
            </div>
            <button onClick={openDefault} data-testid="header-cta" className="hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#1A243A] text-[#FDFBF7] text-sm font-semibold hover:bg-[#D4AF37] hover:text-[#1A243A] transition-colors">{t(T.nav.cta)}</button>
            <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2" data-testid="menu-toggle">{menuOpen ? <X size={22} /> : <Menu size={22} />}</button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-[#FDFBF7] border-t border-[#E7E0D4] px-5 py-4 flex flex-col gap-3 text-sm">
            {["how", "sample", "pricing", "faq"].map((id, i) => (
              <button key={id} onClick={() => { scrollTo(id); setMenuOpen(false); }} className="text-left py-1">{[t(T.nav.how), t(T.nav.sample), t(T.nav.pricing), t(T.nav.faq)][i]}</button>
            ))}
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="top" ref={heroRef} className="relative pt-28 sm:pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 spotlight" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <Reveal><span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-white/60 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-[#8a7220] font-bold"><Sparkles size={13} /> {lang === "pl" ? "Personalizowane książki premium" : "Premium personalized books"}</span></Reveal>
            <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] font-medium">
              {[line1, line2].map((ln, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15 + i * 0.15, ease: [0.22, 1, 0.36, 1] }} className="block">
                    {i === 1 ? <span className="italic text-[#B79CE8]">{ln}</span> : ln}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-6 text-lg text-[#1A243A]/80 max-w-xl leading-relaxed">{t(T.hero.sub)}</motion.p>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85, duration: 0.8 }} className="mt-4 text-sm text-[#64748B] max-w-xl">{t(T.hero.extra)}</motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }} className="mt-9 flex flex-wrap items-center gap-4">
              <button onClick={openDefault} data-testid="hero-primary" className="inline-flex items-center gap-2 rounded-full px-8 py-4 bg-[#1A243A] text-[#FDFBF7] font-semibold text-sm hover:bg-[#D4AF37] hover:text-[#1A243A] hover:scale-[1.02] transition-all">{t(T.hero.primary)} <ArrowRight size={16} /></button>
              <button onClick={() => scrollTo("sample")} data-testid="hero-secondary" className="inline-flex items-center gap-2 rounded-full px-7 py-4 border border-[#1A243A]/20 text-sm font-semibold hover:border-[#D4AF37] transition-colors">{t(T.hero.secondary)}</button>
            </motion.div>
            <div className="mt-8 flex items-center gap-3 text-sm text-[#64748B]">
              <div className="flex text-[#D4AF37]">{[...Array(5)].map((_, i) => <Star key={i} size={15} fill="#D4AF37" />)}</div>
              <span>{lang === "pl" ? "Ponad 2 400 stworzonych historii" : "Over 2,400 stories created"}</span>
            </div>
          </div>
          <motion.div style={{ y: bookY, scale: bookScale }} className="relative">
            <div className="floaty rounded-3xl overflow-hidden shadow-[0_40px_80px_-30px_rgba(26,36,58,0.35)] border border-white">
              <img src={IMG.hero} alt="BOHATERO book" className="w-full h-auto" data-testid="hero-image" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl px-5 py-3 border border-[#E7E0D4] hidden sm:block">
              <p className="text-xs text-[#64748B]">{lang === "pl" ? "Twarda oprawa premium" : "Premium hardcover"}</p>
              <p className="font-display text-lg">32–64 {lang === "pl" ? "stron" : "pages"}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-[#E7E0D4] bg-[#F5F2EB]/60 py-4 overflow-hidden">
        <div className="marquee-track gap-10 text-[#1A243A]/70">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-10 pr-10 font-display text-2xl italic">
              {[lang === "pl" ? "wygląd dziecka" : "your child's look", lang === "pl" ? "zwierzak" : "the pet", lang === "pl" ? "zainteresowania" : "interests", lang === "pl" ? "morał" : "the moral", lang === "pl" ? "dedykacja" : "dedication", lang === "pl" ? "własny świat" : "your own world"].map((w, i) => (
                <span key={i} className="flex items-center gap-10"><span>{w}</span><Sparkles size={16} className="text-[#D4AF37]" /></span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* BENEFITS STRIP */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: BookOpen, pl: ["Twarda oprawa premium", "Trwała jakość, którą chce się mieć na półce."], en: ["Premium hardcover", "Durable quality you'll want to keep on the shelf."] },
            { icon: Camera, pl: ["Ilustracje z Twoich zdjęć", "Bohater naprawdę przypomina Twoje dziecko."], en: ["Illustrations from your photos", "The hero truly resembles your child."] },
            { icon: Truck, pl: ["Wysyłka pod dom", "Starannie zapakowane i dostarczone do Ciebie."], en: ["Delivered to your door", "Carefully packaged and shipped to you."] },
            { icon: Heart, pl: ["Prezent na lata", "Pamiątka, która wzrusza całą rodzinę."], en: ["A gift for years", "A keepsake that moves the whole family."] },
          ].map((b, i) => {
            const Icon = b.icon;
            return (
              <Reveal key={i} delay={i * 0.06}>
                <div className="rounded-3xl border border-[#E7E0D4] bg-white p-6 h-full hover:-translate-y-1 hover:shadow-lg transition-all">
                  <Icon size={26} className="text-[#D4AF37]" strokeWidth={1.5} />
                  <p className="font-display text-lg mt-3 leading-tight">{tr({ pl: b.pl[0], en: b.en[0] }, lang)}</p>
                  <p className="text-sm text-[#64748B] mt-1">{tr({ pl: b.pl[1], en: b.en[1] }, lang)}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* PROCESS */}
      <section id="how" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal><p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold text-center">01 — {lang === "pl" ? "Proces" : "The process"}</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl text-center mt-3">{t(T.process.title)}</h2></Reveal>
        <div className="mt-16 grid md:grid-cols-5 gap-5">
          {T.process.steps.map((s, i) => {
            const icons = [Camera, FileText, Wand2, BookOpen, Printer];
            const imgs = [IMG.proc1, IMG.proc2, IMG.proc3, IMG.proc4, IMG.proc5];
            const Icon = icons[i];
            return (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group rounded-3xl border border-[#E7E0D4] bg-white overflow-hidden hover:-translate-y-1 transition-transform h-full">
                  <div className="aspect-[4/5] overflow-hidden bg-[#F5F2EB]">
                    <img src={imgs[i]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-[#D4AF37]"><Icon size={18} strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-[#64748B]">{lang === "pl" ? "Etap" : "Stage"} {i + 1}</span></div>
                    <p className="font-display text-xl mt-2 leading-tight">{t(s)}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* SAMPLE BOOK */}
      <section id="sample" className="bg-[#F5F2EB]/50 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold">02 — {lang === "pl" ? "Przykładowa książka" : "Sample book"}</p></Reveal>
          <div className="grid lg:grid-cols-2 gap-12 items-center mt-4">
            <div>
              <Reveal delay={0.05}><h2 className="font-display text-4xl sm:text-5xl leading-tight">„Zosia i Tajemnica Doliny Dinozaurów”</h2></Reveal>
              <Reveal delay={0.1}><p className="mt-5 text-[#1A243A]/80 leading-relaxed">{lang === "pl" ? "Zobacz, jak kilka informacji o dziecku zamienia się w pełną przygodę." : "See how a few details about a child turn into a full adventure."}</p></Reveal>
              <Reveal delay={0.15}>
                <div className="mt-6 rounded-3xl bg-white border border-[#E7E0D4] p-6 space-y-1.5 text-sm">
                  {(lang === "pl"
                    ? ["Zosia, 6 lat", "jasne, lekko falowane włosy", "niebieskie oczy", "uwielbia dinozaury, najbardziej triceratopsy", "lubi rysować, ulubiony kolor fioletowy", "ma białego maltańczyka Fado", "śpi z pluszowym królikiem Puszkiem", "boi się ciemności", "morał historii: odwaga"]
                    : ["Zosia, age 6", "light, slightly wavy hair", "blue eyes", "loves dinosaurs, triceratops most of all", "likes drawing, favourite colour purple", "has a white maltese named Fado", "sleeps with a plush rabbit Puszek", "afraid of the dark", "moral of the story: courage"]
                  ).map((l, i) => <p key={i} className="flex items-start gap-2"><span className="text-[#D4AF37] mt-1.5">•</span><span>{l}</span></p>)}
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <div className="grid grid-cols-2 gap-4">
                {[IMG.cover1, IMG.cover2, IMG.cover3, IMG.cover4].map((src, i) => (
                  <div key={i} className={`rounded-2xl overflow-hidden shadow-xl border border-white ${i % 3 === 0 ? "translate-y-4" : ""}`}>
                    <img src={src} alt="Przykładowa książka BOHATERO" className="w-full h-full object-cover aspect-[3/4]" />
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-3">
              {(lang === "pl"
                ? ["Zosia znajduje fioletowe jajko", "wykluwa się mały triceratops", "trafia do Doliny Dinozaurów", "przechodzi przez ciemny las", "spotyka tyranozaura", "pomaga triceratopsowi odnaleźć mamę", "wraca z magicznym fioletowym kamieniem"]
                : ["Zosia finds a purple egg", "a baby triceratops hatches", "she reaches Dinosaur Valley", "crosses the dark forest", "meets a tyrannosaurus", "helps the triceratops find its mum", "returns with a magic purple stone"]
              ).map((s, i) => <span key={i} className="rounded-full bg-white border border-[#E7E0D4] px-4 py-2 text-sm text-[#1A243A]/80">{s}</span>)}
            </div>
            <p className="mt-6 font-display text-2xl italic text-[#B79CE8]">{lang === "pl" ? "Historia wykorzystuje prawdziwe zainteresowania i szczegóły życia dziecka." : "The story uses the child's real interests and life details."}</p>
          </Reveal>
        </div>
      </section>

      <InteriorGallery lang={lang} />
      <QualitySection lang={lang} />

      {/* PERSONALIZATION BENTO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal><p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold">03 — {t(T.personalization.overline)}</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl mt-3 max-w-2xl">{t(T.personalization.title)}</h2></Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {T.personalization.cards.map((c, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className={`rounded-3xl border border-[#E7E0D4] p-8 h-full hover:-translate-y-1 hover:shadow-xl transition-all ${i === 0 ? "bg-[#1A243A] text-[#FDFBF7]" : "bg-white"}`}>
                <span className={`text-xs uppercase tracking-[0.15em] font-bold ${i === 0 ? "text-[#D4AF37]" : "text-[#D4AF37]"}`}>{tr({ pl: c.pl[0], en: c.en[0] }, lang)}</span>
                <p className={`mt-3 font-display text-2xl leading-tight ${i === 0 ? "text-[#FDFBF7]" : ""}`}>{tr({ pl: c.pl[1], en: c.en[1] }, lang)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COMPARISON */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal><p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold text-center">{lang === "pl" ? "Poczuj różnicę" : "Feel the difference"}</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl text-center mt-3 max-w-3xl mx-auto leading-tight">{lang === "pl" ? "To nie zwykła książka z podmienionym imieniem." : "This is not a book with a swapped-in name."}</h2></Reveal>
        <div className="mt-14 grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          <Reveal>
            <div className="rounded-3xl border border-[#E7E0D4] bg-white p-8 h-full">
              <p className="font-display text-2xl text-[#94A3B8]">{lang === "pl" ? "Zwykła personalizacja" : "Ordinary personalization"}</p>
              <ul className="mt-5 space-y-3 text-sm text-[#64748B]">
                {(lang === "pl"
                  ? ["Tylko imię wstawione w gotowy szablon", "Ten sam bohater dla każdego dziecka", "Brak wpływu na fabułę i morał", "Zdjęcia dziecka bez znaczenia", "Zwierzak i pluszak pomijane"]
                  : ["Only a name dropped into a template", "The same hero for every child", "No control over plot or moral", "Child's photos don't matter", "Pet and plush ignored"]
                ).map((l, i) => <li key={i} className="flex items-start gap-3"><X size={17} className="text-[#CBBFA8] mt-0.5 shrink-0" /><span className="line-through decoration-[#CBBFA8]/60">{l}</span></li>)}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border-2 border-[#D4AF37] bg-[#1A243A] text-[#FDFBF7] p-8 h-full shadow-2xl relative overflow-hidden">
              <div className="spotlight absolute inset-0 opacity-40" />
              <p className="font-display text-2xl relative">BOHATERO</p>
              <ul className="mt-5 space-y-3 text-sm relative">
                {(lang === "pl"
                  ? ["Cała historia budowana wokół Twojego dziecka", "Ilustracje inspirowane jego wyglądem", "Ty wybierasz świat, morał i wartości", "Prawdziwe zainteresowania w fabule", "Ukochany zwierzak lub pluszak w przygodzie"]
                  : ["The whole story built around your child", "Illustrations inspired by their look", "You choose the world, moral and values", "Real interests woven into the plot", "A beloved pet or plush in the adventure"]
                ).map((l, i) => <li key={i} className="flex items-start gap-3"><Check size={17} className="text-[#D4AF37] mt-0.5 shrink-0" /><span className="text-[#FDFBF7]/90">{l}</span></li>)}
              </ul>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}><div className="text-center mt-10"><button onClick={openDefault} data-testid="compare-cta" className="inline-flex items-center gap-2 rounded-full px-8 py-4 bg-[#1A243A] text-[#FDFBF7] font-semibold text-sm hover:bg-[#D4AF37] hover:text-[#1A243A] hover:scale-[1.02] transition-all">{lang === "pl" ? "STWÓRZ PRAWDZIWĄ HISTORIĘ" : "CREATE A REAL STORY"} <ArrowRight size={16} /></button></div></Reveal>
      </section>

      {/* WORLDS */}
      <section className="py-24 sm:py-32 bg-[#F5F2EB]/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold">04 — {lang === "pl" ? "Światy i motywy" : "Worlds & themes"}</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl mt-3">{lang === "pl" ? "Wybierz świat przygody" : "Choose a world of adventure"}</h2></Reveal>
        </div>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5 max-w-7xl mx-auto px-5 sm:px-8">
          {WORLDS.map((w, i) => (
            <Reveal key={w.key} delay={i * 0.05}>
              <button onClick={openDefault} data-testid={`world-tile-${w.key}`} className="group relative rounded-3xl overflow-hidden w-full aspect-[3/4] border border-white shadow-lg block">
                {w.img ? <img src={w.img} alt={t(w)} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  : <div className="absolute inset-0 bg-gradient-to-br from-[#E6D7FF] to-[#FDFBF7] flex items-center justify-center"><Sparkles size={40} className="text-[#B79CE8]" /></div>}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A243A]/70 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 right-4 font-display text-xl text-white text-left leading-tight">{t(w)}</span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ANIMATED SCENES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
              <img src={IMG.animScene} alt="" className="w-full aspect-video object-cover" />
              <div className="absolute inset-0 bg-[#1A243A]/25 flex items-center justify-center">
                <button data-testid="play-scene" onClick={() => setScene(IMG.animScene)} className="h-20 w-20 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                  <Play size={30} className="text-[#1A243A] ml-1" fill="#1A243A" />
                </button>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal><p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold">05 — {lang === "pl" ? "Sekcja premium" : "Premium"}</p></Reveal>
            <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl mt-3">{lang === "pl" ? "A co, gdyby historia naprawdę ożyła?" : "What if the story truly came alive?"}</h2></Reveal>
            <Reveal delay={0.15}><p className="mt-5 text-[#1A243A]/80 leading-relaxed">{lang === "pl" ? "W wybranych pakietach otrzymasz również krótkie animowane sceny stworzone na podstawie ilustracji z książki. Filmy dostarczamy cyfrowo — bezpośrednio do Ciebie." : "In selected packages you'll also receive short animated scenes created from the book's illustrations. Clips are delivered digitally — straight to you."}</p></Reveal>
            <Reveal delay={0.2}><div className="mt-6 flex items-center gap-3 text-sm text-[#64748B]"><span className="rounded-full bg-white border border-[#E7E0D4] px-4 py-2">ilustracja</span><ArrowRight size={16} /><span className="rounded-full bg-white border border-[#E7E0D4] px-4 py-2">▶ play</span><ArrowRight size={16} /><span className="rounded-full bg-[#E6D7FF] px-4 py-2">{lang === "pl" ? "animowana scena" : "animated scene"}</span></div></Reveal>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-[#F5F2EB]/50 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold text-center">06 — {lang === "pl" ? "Cennik" : "Pricing"}</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl text-center mt-3">{lang === "pl" ? "Wybierz swój pakiet" : "Choose your package"}</h2></Reveal>
          <div className="mt-14 grid lg:grid-cols-5 md:grid-cols-2 gap-5 items-stretch">
            {PACKAGES.map((p, i) => {
              const featured = p.id === "premium";
              return (
                <Reveal key={p.id} delay={i * 0.06} className={featured ? "lg:-mt-4 lg:mb-[-1rem]" : ""}>
                  <div className={`relative rounded-3xl p-7 h-full flex flex-col ${featured ? "bg-[#1A243A] text-[#FDFBF7] border-2 border-[#D4AF37] shadow-2xl" : "bg-white border border-[#E7E0D4]"}`} data-testid={`pkg-card-${p.id}`}>
                    {p.badge && <span className={`absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1 rounded-full ${featured ? "bg-[#D4AF37] text-[#1A243A]" : "bg-[#E6D7FF] text-[#1A243A]"}`}>{featured && "⭐ "}{t(p.badge)}</span>}
                    <p className={`font-display text-2xl ${featured ? "text-[#FDFBF7]" : ""}`}>{t(p.name)}</p>
                    <p className="mt-3 flex items-baseline gap-1"><span className="font-display text-4xl">{p.price}</span><span className="text-sm text-[#D4AF37] font-semibold">zł</span></p>
                    <ul className={`mt-5 space-y-2 text-sm flex-1 ${featured ? "text-[#FDFBF7]/85" : "text-[#1A243A]/75"}`}>
                      {tr(p.features, lang).map((f, j) => <li key={j} className="flex items-start gap-2"><Star size={13} className="text-[#D4AF37] mt-1 shrink-0" fill="#D4AF37" /><span>{f}</span></li>)}
                    </ul>
                    <button onClick={() => openForm(p)} data-testid={`pkg-cta-${p.id}`} className={`mt-6 w-full rounded-full py-3 text-xs font-bold uppercase tracking-wide transition-colors ${featured ? "bg-[#D4AF37] text-[#1A243A] hover:bg-[#FDFBF7]" : "bg-[#1A243A] text-[#FDFBF7] hover:bg-[#D4AF37] hover:text-[#1A243A]"}`}>{t(p.cta)}</button>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}><p className="text-center mt-8 text-sm text-[#64748B]"><span className="text-[#D4AF37] font-semibold">{lang === "pl" ? "Promocja: -15% na pierwsze zamówienie" : "Deal: -15% on your first order"}</span> · {lang === "pl" ? "zostały ostatnie 3 miejsca w tym tygodniu" : "only 3 slots left this week"}</p></Reveal>
          <Reveal delay={0.25}>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-[#64748B]">
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#D4AF37]" /> {lang === "pl" ? "Bezpieczna płatność" : "Secure payment"}</span>
              <span className="flex items-center gap-2"><Truck size={16} className="text-[#D4AF37]" /> {lang === "pl" ? "Wysyłka w całej Europie" : "Shipping across Europe"}</span>
              <span className="flex items-center gap-2"><Heart size={16} className="text-[#D4AF37]" /> {lang === "pl" ? "Ręcznie tworzona historia" : "Handcrafted story"}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS 4 STEPS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal><h2 className="font-display text-4xl sm:text-5xl">{lang === "pl" ? "Jak to działa" : "How it works"}</h2></Reveal>
        <div className="mt-12 grid md:grid-cols-4 gap-6">
          {(lang === "pl"
            ? [["Wybierasz pakiet", "Wybierz długość książki i dodatki."], ["Opowiadasz nam o dziecku", "Prześlij zdjęcia i kilka informacji."], ["Tworzymy jego przygodę", "Powstaje personalizowana historia i ilustracje."], ["Otrzymujesz gotową książkę", "Książka zostaje wydrukowana i wysłana."]]
            : [["Choose a package", "Pick the book length and extras."], ["Tell us about your child", "Send photos and a few details."], ["We craft the adventure", "A personalized story and illustrations are born."], ["You receive the book", "The book is printed and shipped."]]
          ).map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="relative">
                <span className="font-display text-6xl text-[#E6D7FF]">{i + 1}</span>
                <p className="font-display text-2xl mt-2">{s[0]}</p>
                <p className="mt-2 text-sm text-[#64748B]">{s[1]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GIFT */}
      <section className="py-24 sm:py-32 bg-[#1A243A] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img src={IMG.giftPhoto} alt="" className="w-full aspect-[4/3] object-cover" />
            </div>
          </Reveal>
          <div>
            <Reveal><Gift className="text-[#D4AF37]" size={30} strokeWidth={1.5} /></Reveal>
            <Reveal delay={0.05}><h2 className="font-display text-4xl sm:text-5xl mt-4">{lang === "pl" ? "Prezent, którego nie znajdziesz na półce sklepowej." : "A gift you won't find on any shelf."}</h2></Reveal>
            <Reveal delay={0.1}>
              <div className="mt-6 flex flex-wrap gap-3">
                {(lang === "pl" ? ["urodziny", "święta", "Dzień Dziecka", "prezent od dziadków", "rozpoczęcie szkoły", "pamiątka bez okazji"] : ["birthdays", "holidays", "Children's Day", "a gift from grandparents", "starting school", "a keepsake for no occasion"]).map((g, i) => (
                  <span key={i} className="rounded-full border border-[#FDFBF7]/25 px-4 py-2 text-sm">{g}</span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <OccasionsSection lang={lang} onCta={openDefault} />

      {/* SOCIAL PROOF */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal><div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold">{lang === "pl" ? "Opinie rodziców" : "Parents' reviews"}</p>
            <h2 className="font-display text-4xl sm:text-5xl mt-3">{lang === "pl" ? "Pokochane przez rodziny" : "Loved by families"}</h2>
          </div>
          <div className="flex gap-8">
            <div><p className="font-display text-4xl text-[#D4AF37]">4.9/5</p><p className="text-sm text-[#64748B]">{lang === "pl" ? "średnia ocen" : "average rating"}</p></div>
            <div><p className="font-display text-4xl text-[#D4AF37]">2 400+</p><p className="text-sm text-[#64748B]">{lang === "pl" ? "historii" : "stories"}</p></div>
          </div>
        </div></Reveal>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {T.reviews.map((r, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className="rounded-3xl bg-white border border-[#E7E0D4] p-6 h-full">
                <div className="flex text-[#D4AF37]">{[...Array(r.stars)].map((_, j) => <Star key={j} size={15} fill="#D4AF37" />)}</div>
                <p className="mt-4 text-[#1A243A]/85 leading-relaxed">„{lang === "pl" ? r.pl : r.en}”</p>
                <p className="mt-4 text-sm font-semibold">{r.name} <span className="text-[#64748B] font-normal">· {r.city}</span></p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <ReviewsMarquee lang={lang} />

      {/* FAQ */}
      <section id="faq" className="bg-[#F5F2EB]/50 py-24 sm:py-32">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal><h2 className="font-display text-4xl sm:text-5xl text-center">FAQ</h2></Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10">
              {T.faq.map((f, i) => (
                <AccordionItem key={i} value={`i${i}`} data-testid={`faq-${i}`} className="border-b border-[#E7E0D4]">
                  <AccordionTrigger className="font-display text-xl text-left hover:no-underline hover:text-[#D4AF37]">{t(f.q)}</AccordionTrigger>
                  <AccordionContent className="text-[#1A243A]/75 leading-relaxed text-base">{t(f.a)}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.1}><p className="mt-8 flex items-center justify-center gap-2 text-sm text-[#64748B]"><ShieldCheck size={16} className="text-[#D4AF37]" /> {lang === "pl" ? "Prywatność dzieci traktujemy z najwyższą starannością." : "We treat children's privacy with the utmost care."}</p></Reveal>
        </div>
      </section>

      <NewsletterSection lang={lang} />

      {/* FINAL CTA */}
      <section className="py-28 sm:py-40 relative overflow-hidden">
        <div className="absolute inset-0 spotlight" />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center relative">
          <Reveal><h2 className="font-display text-4xl sm:text-6xl leading-tight">{lang === "pl" ? "Każde dziecko zasługuje na historię, w której to ono jest bohaterem." : "Every child deserves a story where they are the hero."}</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-6 text-lg text-[#1A243A]/80">{lang === "pl" ? "Stwórz książkę, która może zostać z nim na lata." : "Create a book that can stay with them for years."}</p></Reveal>
          <Reveal delay={0.2}><button onClick={openDefault} data-testid="final-cta" className="mt-10 inline-flex items-center gap-2 rounded-full px-10 py-5 bg-[#1A243A] text-[#FDFBF7] font-semibold hover:bg-[#D4AF37] hover:text-[#1A243A] hover:scale-[1.03] transition-all">{lang === "pl" ? "STWÓRZ JEGO HISTORIĘ" : "CREATE THEIR STORY"} <ArrowRight size={18} /></button></Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#1A243A] text-[#FDFBF7]/80 pt-16 pb-28 md:pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2"><Sparkles className="text-[#D4AF37]" size={20} /><span className="font-display text-2xl text-[#FDFBF7]">BOHATERO</span></div>
            <p className="mt-3 text-sm text-[#FDFBF7]/60">{lang === "pl" ? "Twoje dziecko. Jego historia." : "Your child. Their story."}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">{lang === "pl" ? "Firma" : "Company"}</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/info/about" data-testid="footer-about" className="hover:text-[#D4AF37] transition-colors">{t(T.footer.about)}</Link></li>
              <li><Link to="/info/contact" data-testid="footer-contact" className="hover:text-[#D4AF37] transition-colors">{t(T.footer.contact)}</Link></li>
              <li><button onClick={() => scrollTo("faq")} className="hover:text-[#D4AF37] transition-colors">{t(T.footer.faq)}</button></li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">{lang === "pl" ? "Informacje" : "Info"}</p>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/info/regulamin" data-testid="footer-terms" className="hover:text-[#D4AF37] transition-colors">{t(T.footer.terms)}</Link></li>
              <li><Link to="/info/prywatnosc" data-testid="footer-privacy" className="hover:text-[#D4AF37] transition-colors">{t(T.footer.privacy)}</Link></li>
              <li><Link to="/info/dostawa" data-testid="footer-delivery" className="hover:text-[#D4AF37] transition-colors">{t(T.footer.delivery)}</Link></li>
              <li><Link to="/info/zwroty" data-testid="footer-returns" className="hover:text-[#D4AF37] transition-colors">{t(T.footer.returns)}</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">Social</p>
            <ul className="mt-4 space-y-2 text-sm">{["Instagram", "Facebook", "TikTok", "YouTube"].map((x, i) => <li key={i}><a href="#" className="hover:text-[#D4AF37] transition-colors">{x}</a></li>)}</ul>
          </div>
        </div>
        <p className="max-w-7xl mx-auto px-5 sm:px-8 mt-12 text-xs text-[#FDFBF7]/40">© {new Date().getFullYear()} BOHATERO. {lang === "pl" ? "Wszelkie prawa zastrzeżone." : "All rights reserved."}</p>
      </footer>

      {/* STICKY MOBILE CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-[#FDFBF7]/90 backdrop-blur-lg border-t border-[#E7E0D4]">
        <button onClick={openDefault} data-testid="sticky-cta" className="w-full rounded-full py-4 bg-[#1A243A] text-[#FDFBF7] font-semibold">{t(T.nav.cta)}</button>
      </div>

      {selected && <OrderForm pkg={selected} onClose={() => setSelected(null)} />}
      <VideoLightbox open={!!scene} image={scene} onClose={() => setScene(null)} caption={{ close: lang === "pl" ? "Zamknij" : "Close", text: lang === "pl" ? "Podgląd animowanej sceny · pełne filmy dostarczamy cyfrowo w wybranych pakietach." : "Animated scene preview · full clips are delivered digitally in selected packages." }} />
    </div>
  );
}
